import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => React.ReactNode;
  /** "right" para tabular-nums alineados a la derecha */
  align?: "left" | "right";
  /** "min" para apretar a su contenido */
  width?: "min" | "auto";
}

interface Props<T extends { id: number }> {
  rows: T[];
  columns: Column<T>[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowClick?: (row: T) => void;
  selectedId?: number | null;
  emptyLabel?: string;
}

export function ApplicationsTable<T extends { id: number }>({
  rows,
  columns,
  page,
  pageSize,
  total,
  onPageChange,
  onRowClick,
  selectedId,
  emptyLabel = "No hay postulaciones que coincidan con los filtros.",
}: Props<T>) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="nx-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="nx-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(col.align === "right" && "text-right")}
                  style={col.width === "min" ? { width: 1, whiteSpace: "nowrap" } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-fg-subtle py-8">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    onRowClick && "cursor-pointer",
                    selectedId === row.id && "bg-accent-soft/30",
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        col.align === "right" && "text-right tabular-nums",
                      )}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-bg-surface">
        <span className="text-2xs text-fg-subtle tabular-nums">
          {total === 0
            ? "0 resultados"
            : `${from}–${to} de ${total.toLocaleString("es-CL")}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="nx-btn-ghost px-2 py-1"
            aria-label="Página anterior"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-2xs text-fg-subtle px-2 tabular-nums">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="nx-btn-ghost px-2 py-1"
            aria-label="Página siguiente"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
